import { type RouteSectionProps, useCurrentMatches, useLocation, useNavigate } from "@solidjs/router";
import { HttpStatusCode } from "@solidjs/start";
import type { PostAuthAction } from "@storytell/auth";
import { getIdPrefix, isPageSlug } from "@storytell/core";
import type { StreamingChatId } from "@storytell/streaming";
import { ROLE_OWNER } from "@storytell/types";
import { StTooltipSuppressionProvider } from "@storytell/ui/tooltip";
import { createEffect, createSignal, onCleanup, onMount, type ParentComponent, Show, Suspense } from "solid-js";
import { isServer } from "solid-js/web";
import { ClientSideSplashScreen } from "~/ClientSideSplashScreen";
import { AppLayout } from "~/components/layouts/AppLayout";
import { GlobalCommandPalette } from "~/components/prompt/internal/components/GlobalCommandPalette";
import { SharedChatSSRMeta } from "~/components/shared/SharedChatSSRMeta";
import { SharedLiveSessionSSRMeta } from "~/components/shared/SharedLiveSessionSSRMeta";
import { SignUpSSRMeta } from "~/components/shared/SignUpSSRMeta";
import { guestSignInHref } from "~/config/authGating";
import { useHelpTooltipsPreference } from "~/hooks/useHelpTooltipsPreference";
import { useSyncUserLocale } from "~/hooks/useSyncUserLocale";
import { useWindowWidth } from "~/hooks/useWindowWidth";
import { useI18n } from "~/i18n/context";
import { useGoogleConnect } from "~/lib/integrations";
import { lazy } from "~/lib/lazyWithReload";
import { ownedOrgTenant } from "~/lib/ownedOrgTenant";
import { useWire, WireProvider } from "~/wire";
import { pageSlugDocumentTitle } from "./(app)/_pageTitles";

// Lazy so a stalled modal chunk can't pin the root Suspense and hang the boot
// splash. They render under a Suspense fallback={null} below; none is needed
// on first paint. AppLayout stays eager — it is the boot UI itself.
const InsufficientCreditsModal = lazy(() =>
  import("~/components/billing/InsufficientCreditsModal").then((m) => ({ default: m.InsufficientCreditsModal }))
);
const InsufficientPermissionsModal = lazy(() =>
  import("~/components/billing/InsufficientPermissionsModal").then((m) => ({ default: m.InsufficientPermissionsModal }))
);
const DisallowedModal = lazy(() =>
  import("~/components/chat/DisallowedModal").then((m) => ({ default: m.DisallowedModal }))
);
const AssetsProcessingWaitModal = lazy(() =>
  import("~/components/assets").then((m) => ({ default: m.AssetsProcessingWaitModal }))
);
const StripeReturnModal = lazy(() =>
  import("~/components/billing/StripeReturnModal").then((m) => ({ default: m.StripeReturnModal }))
);
const ManageLabelsModal = lazy(() =>
  import("~/components/labels/ManageLabelsModal").then((m) => ({ default: m.ManageLabelsModal }))
);
const SignInRequiredModal = lazy(() =>
  import("~/components/auth/SignInRequiredModal").then((m) => ({ default: m.SignInRequiredModal }))
);
const ProjectSwitchConfirmModal = lazy(() =>
  import("~/components/loading/ProjectSwitchConfirmModal").then((m) => ({ default: m.ProjectSwitchConfirmModal }))
);
const ProjectSwitchStreamingModal = lazy(() =>
  import("~/components/loading/ProjectSwitchStreamingModal").then((m) => ({ default: m.ProjectSwitchStreamingModal }))
);
const AiLinkWarningDialog = lazy(() =>
  import("~/components/chat/aiLinkWarning").then((m) => ({ default: m.AiLinkWarningDialog }))
);
const DesktopAppBanner = lazy(() =>
  import("~/components/shared/DesktopAppBanner").then((m) => ({ default: m.DesktopAppBanner }))
);

const StTooltipSuppressionBridge: ParentComponent = (props) => {
  const { showStTooltips } = useHelpTooltipsPreference();
  const suppressed = () => !isServer && !showStTooltips();
  return <StTooltipSuppressionProvider suppressed={suppressed}>{props.children}</StTooltipSuppressionProvider>;
};

/**
 * Dev-only: throw when ?error is in the URL so the ErrorBoundary fallback is shown.
 * Visit http://localhost:3000/?error to test the "This wasn't your fault" screen.
 */
const TriggerErrorBoundaryForDev = () => {
  const location = useLocation();
  if (typeof window !== "undefined" && location.query.error != null) {
    throw new Error("Dev: show error boundary");
  }
  return null;
};

const isDriveConnectRequest = (action: PostAuthAction): boolean =>
  action === "connect-google-drive" ||
  action === "connect-google-drive:ready" ||
  action === "connect-google-drive:retry";

const PostAuthRedirect = () => {
  const wire = useWire();
  const navigate = useNavigate();
  const location = useLocation();
  const googleConnect = useGoogleConnect();

  createEffect(() => {
    const provider = location.query.provider;
    if (provider !== "google") return;
    // The backend only sends the app back here once the token exchange has
    // succeeded, so a Google return means Drive is connected however the user
    // got there — including by hand from the chat card, which never touches a
    // stored request. Drop it rather than send an already-connected user to the
    // consent screen again. The clear happens while the query is still readable.
    if (isDriveConnectRequest(wire.auth.postAuthAction())) {
      wire.auth.setPostAuthAction(undefined);
    }
    window.history.replaceState({}, "", window.location.pathname);
    // The consent flow ran under the organization the user is working in, so
    // that is the one whose integrations page now has something new on it.
    const orgId = ownedOrgTenant(wire.tenants.tenants(), wire.currentOrgId())?.organizationId;
    navigate(orgId ? `/orgs/${orgId}/integrations` : "/");
  });

  // signedUp marks the one sync that turned a guest account into a real one. A
  // guest who abandons the sign-up and later signs in to an account they already
  // had never gets the request promoted, so nothing hands them to Google.
  createEffect(() => {
    if (wire.auth.postAuthAction() !== "connect-google-drive") return;
    if (!wire.auth.authenticatedUser()?.signedUp) return;
    wire.auth.setPostAuthAction("connect-google-drive:ready");
  });

  // Clearing before the hand-off avoids a re-prompt when the full-page redirect
  // into Google races a later clear, so a first failure puts the request back as
  // the one-more-go value for the next page load. A failure spending that value
  // puts nothing back: each write renews the 15 minutes, so a request that
  // restored itself would be retried on every page load for ever, silently. That
  // restore re-arms the same signal the resume reads, hence the cap of one
  // attempt per load.
  let resumedDriveConnect = false;
  const resumeDriveConnect = async (action: PostAuthAction) => {
    resumedDriveConnect = true;
    wire.auth.setPostAuthAction(undefined);
    let failed = false;
    try {
      await googleConnect.connect();
      failed = googleConnect.error() !== null;
    } catch {
      failed = true;
    }
    if (failed && action === "connect-google-drive:ready") {
      wire.auth.setPostAuthAction("connect-google-drive:retry");
    }
  };

  createEffect(() => {
    const action = wire.auth.postAuthAction();
    if (wire.auth.isRegistered()) {
      if (action?.startsWith("refill:")) {
        const amount = Number.parseInt(action.split(":")[1], 10);
        navigate("/upgrade", { state: { refill: amount } });
        wire.auth.setPostAuthAction(undefined);
        return;
      }

      if (action?.startsWith("subscribe-pro:")) {
        const period = action.split(":")[1] as "monthly" | "yearly";
        navigate("/upgrade", { state: { subscribe: period } });
        wire.auth.setPostAuthAction(undefined);
        return;
      }

      if (action?.startsWith("subscribe-power-user:")) {
        const period = action.split(":")[1] as "monthly" | "yearly";
        navigate("/upgrade", { state: { subscribePowerUser: period } });
        wire.auth.setPostAuthAction(undefined);
        return;
      }

      // The connect request is project-scoped, so wait for the project to
      // resolve — right after sign-up there is none. isGuest is re-checked
      // because connect() sends guests back to sign-up. A Google return is
      // excluded so the resume cannot outrun the clear that return performs.
      if (
        (action === "connect-google-drive:ready" || action === "connect-google-drive:retry") &&
        location.query.provider !== "google" &&
        !resumedDriveConnect &&
        !wire.auth.isGuest() &&
        wire.currentProjectId()
      ) {
        void resumeDriveConnect(action);
        return;
      }
    }
  });

  return null;
};

const UNFOLD_SIDE = "scope-picker";
const UNFOLD_PANEL_TAB = "sections";

const hasUnfoldParams = (q: Record<string, unknown>): boolean => {
  const side = q.side;
  const panelTab = q.panelTab;
  return (
    side === UNFOLD_SIDE &&
    (panelTab === UNFOLD_PANEL_TAB || (Array.isArray(panelTab) && panelTab[0] === UNFOLD_PANEL_TAB))
  );
};

const queryHasNonEmptyString = (raw: unknown): boolean => {
  const s: unknown = Array.isArray(raw) ? raw[0] : raw;
  return typeof s === "string" && s.trim() !== "";
};

const hasSharedPromptUrlQuery = (q: Record<string, unknown>): boolean =>
  queryHasNonEmptyString(q.chat) || queryHasNonEmptyString(q.prompt);

// On /app a registered user waits for the persisted last-project id from
// /users/me before redirecting. If neither that nor an org ever resolves the
// user would be stuck on a loading surface, so after this deadline we drop to
// the root instead.
const HOME_REDIRECT_TIMEOUT_MS = 8_000;

/** Redirects authed users on / to their project home, and /app to a useful destination. */
const DefaultHomeRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useWindowWidth();
  const wire = useWire();
  const [timedOut, setTimedOut] = createSignal(false);

  onMount(() => {
    if (isServer) return;
    const timer = setTimeout(() => setTimedOut(true), HOME_REDIRECT_TIMEOUT_MS);
    onCleanup(() => clearTimeout(timer));
  });

  createEffect(() => {
    const pathname = location.pathname;
    const isRootHome = pathname === "/";
    const isAppHome = pathname === "/app";
    if (!isRootHome && !isAppHome) return;

    const q = location.query as Record<string, unknown>;

    // /app always redirects to a useful destination — no "stay put" branch.
    // Handled here (not in routes/(app)/app.tsx) because AppLayout doesn't
    // mount route children for non-standalone/non-navbar-only paths.
    if (isAppHome) {
      const signIn = guestSignInHref(wire.auth);
      if (signIn) {
        navigate(signIn, { replace: true });
        return;
      }
      if (!wire.auth.isReady() || !wire.auth.isRegistered()) return;
      const defaultPid = wire.user.defaultProjectId();
      if (defaultPid) {
        const params = new URLSearchParams(location.search);
        const qs = params.toString();
        navigate(qs ? `/project/${defaultPid}?${qs}` : `/project/${defaultPid}`, { replace: true });
        return;
      }
      // /orgs/{id} is owner-only, so fall back only to an org the user owns at
      // org scope — never just the first reachable tenant, which can be a
      // project's owning org the user can't open (ENG-6478).
      const tenants = wire.tenants.tenants();
      const orgId = tenants.find((t) => t.organizationRole === ROLE_OWNER)?.organizationId;
      if (orgId) {
        navigate(`/orgs/${orgId}`, { replace: true });
        return;
      }
      // Neither a default project nor an org resolved. After the deadline, drop
      // to the root so the user isn't stranded on /app forever; before it, keep
      // waiting for one of them to land.
      if (timedOut()) {
        navigate("/", { replace: true });
      }
      return;
    }

    if (!isRootHome) return;

    if (hasSharedPromptUrlQuery(q)) {
      return;
    }

    if (wire.auth.isReady() && wire.auth.isRegistered()) {
      // No URL projectId — use the persisted "last project" from /users/me.
      // Brand-new users with no default stay on "/" so the no-projects UI shows.
      const projectId = wire.user.defaultProjectId();
      if (projectId) {
        const params = new URLSearchParams(location.search);
        const qs = params.toString();
        navigate(qs ? `/project/${projectId}?${qs}` : `/project/${projectId}`, { replace: true });
      }
      return;
    }

    if (isMobile()) return;

    const hasSideHidden = q.sideHidden === "true" || (Array.isArray(q.sideHidden) ? q.sideHidden[0] === "true" : false);

    const projectId = wire.user.defaultProjectId();
    const projectAssetCount = projectId ? wire.assets.all().filter((a) => a.projectId === projectId).length : 0;
    if (projectAssetCount >= 1) {
      if (hasUnfoldParams(q) && hasSideHidden) {
        const cleanParams = new URLSearchParams(location.search);
        cleanParams.delete("side");
        cleanParams.delete("panelTab");
        const cleanSearch = cleanParams.toString();
        navigate(cleanSearch ? `/?${cleanSearch}` : "/", { replace: true });
      }
      return;
    }
  });

  return null;
};

/**
 * Sets `document.title` reactively based on current navigation state.
 * Chat pages show the chat title, library pages show the section name.
 */
const DynamicDocumentTitle = () => {
  const wire = useWire();
  const { t } = useI18n();
  const location = useLocation();

  createEffect(() => {
    if (isServer) return;

    // /auth/signup owns its title via SignUpSSRMeta's <Title>; without a
    // primaryId this effect would otherwise reset it to "Chief" once the
    // splash lifts, so crawlers running JS would see the generic title.
    if (location.pathname === "/auth/signup") return;

    const id = wire.navigation.state().primaryId;
    if (!id) {
      document.title = "Chief";
      return;
    }

    // Page slugs (assets, chats, skills, etc.)
    if (isPageSlug(id)) {
      document.title = pageSlugDocumentTitle(id, t);
      return;
    }

    // Chat entities — show the chat title
    const prefix = getIdPrefix(id);
    if (prefix === "chat") {
      const chatTitle = wire.chatTitle.getChatTitle(id as StreamingChatId);
      document.title = chatTitle ? `${chatTitle} | Chief` : "Chief";
      return;
    }

    document.title = "Chief";
  });

  return null;
};

const SyncUserLocale = () => {
  useSyncUserLocale();
  return null;
};

// ClientSideSplashScreen renders routes only after mount, so the catch-all's page never
// runs on the server and could not set its own status: an unknown path answered 200.
const NotFoundStatus = () => {
  const matches = useCurrentMatches();
  return (
    <Show when={matches().at(-1)?.route.pattern.endsWith("*404")}>
      <HttpStatusCode code={404} />
    </Show>
  );
};

export default function AppRouteLayout(props: RouteSectionProps) {
  return (
    <>
      <TriggerErrorBoundaryForDev />
      {/* Render meta tags outside ClientSideSplashScreen for SSR */}
      <SharedChatSSRMeta />
      <SharedLiveSessionSSRMeta />
      <SignUpSSRMeta />
      <NotFoundStatus />
      <WireProvider>
        <ClientSideSplashScreen>
          <StTooltipSuppressionBridge>
            <DynamicDocumentTitle />
            <SyncUserLocale />
            <PostAuthRedirect />
            <DefaultHomeRedirect />
            <AppLayout>{props.children}</AppLayout>
            {/* Global modals - rendered outside AppLayout so they're always visible.
                One Suspense boundary keeps a stalled modal chunk from pinning the
                root Suspense; fallback={null} since none paints on first load. */}
            <Suspense fallback={null}>
              <InsufficientCreditsModal />
              <InsufficientPermissionsModal />
              <DisallowedModal />
              <AssetsProcessingWaitModal />
              <StripeReturnModal />
              <ManageLabelsModal />
              <SignInRequiredModal />
              <ProjectSwitchConfirmModal />
              <ProjectSwitchStreamingModal />
              <AiLinkWarningDialog />
              <DesktopAppBanner />
              {/* Eager, unlike the modals above: it only binds the shortcut, and lazy-loads
                  the palette itself on first use. Sits here rather than in AppLayout so it
                  also covers the routes AppLayout renders without its standard chrome. */}
              <GlobalCommandPalette />
            </Suspense>
          </StTooltipSuppressionBridge>
        </ClientSideSplashScreen>
      </WireProvider>
    </>
  );
}
